/**
 * Sign-in step two: exchange the code for a session.
 *
 * The resend button counts down the documented 60 second cooldown locally so the
 * user is not surprised by a `rate_limited` response, and the development code
 * is shown when the API echoes it.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { VERIFICATION_CODE_RESEND_COOLDOWN_SECONDS } from '@shared/constants'
import { sanitizeRedirectPath } from '@shared/redirect'
import { isValidVerificationCode } from '@shared/validation'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { useSendCode, useVerifyCode } from '../../hooks/use-auth'
import { currentLocale } from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'

export function LoginVerifyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const verifyCode = useVerifyCode()
  const sendCode = useSendCode()

  const email = searchParams.get('email') ?? ''
  // Sanitised with the same rule the Worker signs the target with: a
  // `//host` or backslash target would otherwise leave the site after a
  // successful sign-in.
  const redirect = sanitizeRedirectPath(searchParams.get('redirect'))
  const locale = currentLocale()

  const [code, setCode] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(VERIFICATION_CODE_RESEND_COOLDOWN_SECONDS)

  useEffect(() => {
    if (cooldown <= 0) {
      return
    }
    const timer = window.setInterval(() => {
      setCooldown((value) => (value <= 1 ? 0 : value - 1))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [cooldown])

  useEffect(() => {
    if (!email) {
      navigate('/login/email', { replace: true })
    }
  }, [email, navigate])

  function submit() {
    if (!isValidVerificationCode(code)) {
      setLocalError(t('auth.invalidCode'))
      return
    }
    setLocalError(null)

    verifyCode.mutate(
      { email, code: code.trim(), locale },
      {
        onSuccess: () => {
          navigate(redirect, { replace: true })
        },
      },
    )
  }

  function resend() {
    sendCode.mutate(
      { email, locale },
      {
        onSuccess: (result) => {
          setCooldown(result.resendAfterSeconds)
        },
      },
    )
  }

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the form silent.
  const apiError = mutationErrorText(verifyCode.error, t)
  const resendError = mutationErrorText(sendCode.error, t)

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('auth.loginTitle'), to: '/login/email' },
          { label: t('auth.verifyStepTitle') },
        ]}
        title={t('auth.verifyStepTitle')}
        description={t('auth.verifyStepBody', { email })}
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-4 sm:pt-5">
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <FieldGroup>
              <Field
                label={t('auth.codeLabel')}
                htmlFor="code"
                required
                error={localError}
                hint={t('auth.codePlaceholder')}
              >
                <Input
                  id="code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  aria-invalid={Boolean(localError)}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  className="tracking-[0.5em]"
                />
              </Field>
            </FieldGroup>

            {apiError ? (
              <ErrorAlert message={apiError} />
            ) : null}

            <Button type="submit" disabled={verifyCode.isPending}>
              {verifyCode.isPending ? t('auth.verifying') : t('auth.verify')}
            </Button>
          </form>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/login/email">{t('auth.changeEmail')}</Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={cooldown > 0 || sendCode.isPending}
              onClick={resend}
            >
              {cooldown > 0 ? t('auth.resendIn', { seconds: cooldown }) : t('auth.resend')}
            </Button>
          </div>

          {resendError ? (
            <ErrorAlert message={resendError} />
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
